import { type ReactNode } from 'react'
import { useForm, Head, Link, usePage, router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { TagCombobox } from '@/components/TagCombobox'
import ImageUpload from '@/components/ImageUpload'
import GoogleOAuthButton from '@/components/GoogleOAuthButton'
import type { FormEvent } from 'react'
import type { SettingsOrganization, SharedProps } from '@/types'

type DashboardSettingsProps = {
  organization: SettingsOrganization
  cause_suggestions: string[]
  network_suggestions: string[]
  selection_limit: number
  custom_entry_limit: number
}

export default function DashboardSettings({
  organization,
  cause_suggestions: causeSuggestions,
  network_suggestions: networkSuggestions,
  selection_limit: selectionLimit,
  custom_entry_limit: customEntryLimit,
}: DashboardSettingsProps) {
  const { current_organization: currentOrganization } = usePage<SharedProps>().props
  const { data, setData, patch, processing, errors, transform } = useForm({
    name: organization.name,
    description: organization.description ?? '',
    cause_description: organization.cause_description ?? '',
    website: organization.website ?? '',
    phone: organization.phone ?? '',
    donation_url: organization.donation_url ?? '',
    primary_cause: organization.primary_cause ?? '',
    causes: organization.causes ?? [],
    industries: organization.industries ?? [],
    logo: null as string | null,
    remove_logo: false,
    show_email: organization.show_email,
    password: '',
    password_confirmation: '',
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    transform(formData => ({ organization: formData }))
    patch('/dashboard/settings')
  }


  return (
    <>
      <Head title="Organization Settings" />
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="mb-8 text-4xl font-semibold tracking-tight">Settings</h1>

        <form onSubmit={submit} className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Basic Info</CardTitle>
              <CardDescription>Your organization's name and contact</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization name</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={e => setData('name', e.target.value)}
                />
                {errors.name && (
                  <p className="text-base text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={organization.email} disabled className="opacity-60" />
                <p className="text-sm text-muted-foreground">Contact support to change your email.</p>
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="show_email">Show email on public profile</Label>
                <Switch
                  id="show_email"
                  checked={data.show_email}
                  onCheckedChange={checked => setData('show_email', checked)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Logo */}
          <Card>
            <CardHeader>
              <CardTitle>Logo</CardTitle>
              <CardDescription>Your organization's logo shown on your profile and events</CardDescription>
            </CardHeader>
            <CardContent>
              <ImageUpload
                currentUrl={organization.logo_url}
                onUpload={(signedId) => {
                  if (signedId) {
                    setData('logo', signedId)
                    setData('remove_logo', false)
                  } else {
                    setData('logo', null)
                    setData('remove_logo', true)
                  }
                }}
              />
            </CardContent>
          </Card>

          {/* About */}
          <Card>
            <CardHeader>
              <CardTitle>About</CardTitle>
              <CardDescription>Tell attendees about your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={data.description}
                  onChange={e => setData('description', e.target.value)}
                  rows={4}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cause_description">Cause description</Label>
                <Textarea
                  id="cause_description"
                  value={data.cause_description}
                  onChange={e => setData('cause_description', e.target.value)}
                  placeholder="Describe your cause in your own words..."
                  rows={2}
                />
                <p className="text-sm text-muted-foreground">We use AI to categorize your organization based on this description.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={data.website}
                  onChange={e => setData('website', e.target.value)}
                  placeholder="https://example.org"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={data.phone}
                  onChange={e => setData('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="donation_url">Donation URL</Label>
                <Input
                  id="donation_url"
                  type="url"
                  value={data.donation_url}
                  onChange={e => setData('donation_url', e.target.value)}
                  placeholder="https://donate.example.org"
                />
              </div>
            </CardContent>
          </Card>

          {/* Causes & Professional Networks */}
          <Card>
            <CardHeader>
              <CardTitle>Causes & Professional Networks</CardTitle>
              <CardDescription>Help attendees find you by selecting relevant categories</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label htmlFor="primary_cause">Primary cause</Label>
                <Input
                  id="primary_cause"
                  value={data.primary_cause}
                  onChange={e => setData('primary_cause', e.target.value)}
                  placeholder="e.g., Children's education"
                />
              </div>

              <p className="text-sm text-muted-foreground">
                Select up to {selectionLimit} of each. Don't see one? Type your own — we'll review and add it.
              </p>

              <TagCombobox
                label="Causes"
                placeholder="Search or add a cause..."
                suggestions={causeSuggestions}
                selected={data.causes}
                onChange={(items) => setData('causes', items.slice(0, selectionLimit))}
                allowCustom
                maxItems={selectionLimit}
                maxLength={customEntryLimit}
                error={errors.causes}
              />

              <TagCombobox
                label="Professional Networks"
                placeholder="Search or add a network..."
                suggestions={networkSuggestions}
                selected={data.industries}
                onChange={(items) => setData('industries', items.slice(0, selectionLimit))}
                allowCustom
                maxItems={selectionLimit}
                maxLength={customEntryLimit}
                error={errors.industries}
              />

              {organization.ai_keywords.length > 0 && (
                <div className="space-y-3">
                  <Label>AI-generated search terms</Label>
                  <p className="text-sm text-muted-foreground">Automatically generated to help people find you.</p>
                  <div className="flex flex-wrap gap-2">
                    {organization.ai_keywords.map(keyword => (
                      <Badge key={keyword} variant="secondary">{keyword}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Connected Accounts */}
          <Card>
            <CardHeader>
              <CardTitle>Connected Accounts</CardTitle>
              <CardDescription>Link your Google account for one-click sign in</CardDescription>
            </CardHeader>
            <CardContent>
              {organization.google_linked ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <svg className="size-5" viewBox="0 0 24 24">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium">Google</p>
                      <p className="text-sm text-muted-foreground">Connected</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => router.delete('/settings/oauth/google_oauth2')}
                    disabled={!organization.has_password}
                  >
                    Unlink
                  </Button>
                </div>
              ) : (
                <GoogleOAuthButton
                  accountType="organization"
                  label="Link Google Account"
                  mode="button"
                />
              )}
              {organization.google_linked && !organization.has_password && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Set a password before unlinking Google
                </p>
              )}
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>Leave blank to keep your current password</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password"
                  type="password"
                  value={data.password}
                  onChange={e => setData('password', e.target.value)}
                  autoComplete="new-password"
                />
                {errors.password && (
                  <p className="text-base text-destructive">{errors.password}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password_confirmation">Confirm new password</Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  value={data.password_confirmation}
                  onChange={e => setData('password_confirmation', e.target.value)}
                  autoComplete="new-password"
                />
                {errors.password_confirmation && (
                  <p className="text-base text-destructive">{errors.password_confirmation}</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Button type="submit" disabled={processing} className="w-full">
            Save Changes
          </Button>
        </form>

        {currentOrganization && !currentOrganization.pro && (
          <Card className="mt-6">
            <CardContent className="flex items-center justify-between py-4">
              <div>
                <p className="text-base font-medium">Unlock Pro features</p>
                <p className="text-base text-muted-foreground">Get priority placement, analytics, and more.</p>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link href="/dashboard/billing">Upgrade</Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

DashboardSettings.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
